import React, { useCallback, useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../context/AuthContext';

const STATUS_OPTIONS = ['pending', 'reviewing', 'approved', 'declined'];

export default function AdminSellerApplicationsPage() {
  const { token } = useContext(AuthContext);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadApplications = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/admin/seller-applications', { headers: { 'x-auth-token': token } });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.msg || 'Seller applications could not be loaded.');
      setApplications(Array.isArray(data.applications) ? data.applications : []);
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) loadApplications();
  }, [loadApplications, token]);

  const updateApplication = async (application) => {
    setSavingId(application.id);
    setError('');
    setMessage('');
    try {
      const response = await fetch(`/api/admin/seller-applications/${application.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
        body: JSON.stringify({ status: application.status, adminNote: application.admin_note || '' }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.msg || 'Application could not be updated.');
      setApplications((current) => current.map((item) => item.id === application.id ? data.application : item));
      setMessage(`Application ${application.application_number} updated.`);
    } catch (updateError) {
      setError(updateError.message);
    } finally {
      setSavingId(null);
    }
  };

  const updateLocal = (id, field, value) => {
    setApplications((current) => current.map((item) => item.id === id ? { ...item, [field]: value } : item));
  };

  return (
    <div className="admin-seller-page container-fluid py-4">
      <div className="admin-cms-page-header d-flex justify-content-between align-items-center mb-4">
        <div>
          <p className="admin-about-page__eyebrow">Marketplace makers</p>
          <h2 className="brand-font m-0">Seller applications</h2>
          <span className="text-muted small">Review makers who want to sell through Hunar.</span>
        </div>
        <button type="button" className="btn btn-outline-custom rounded-pill" onClick={loadApplications} disabled={loading}>Refresh</button>
      </div>

      {message && <div className="alert alert-success py-2 px-3 small">{message}</div>}
      {error && <div className="alert alert-danger py-2 px-3 small">{error}</div>}
      {loading ? <div role="status">Loading applications…</div> : applications.length === 0 ? (
        <div className="border rounded bg-card p-5 text-center">No seller applications yet.</div>
      ) : (
        <div className="seller-applications-grid">
          {applications.map((application) => {
            const photos = Array.isArray(application.photo_urls) ? application.photo_urls : [];
            return (
              <article className="seller-application-card" key={application.id}>
                <div className="seller-application-card__header">
                  <div>
                    <span className="small text-muted">{application.application_number}</span>
                    <h3>{application.name}</h3>
                    <p className="text-muted small mb-0">{application.city}, {application.state}{application.age ? ` · Age ${application.age}` : ''}</p>
                  </div>
                  <select className="form-select form-select-sm" value={application.status} onChange={(event) => updateLocal(application.id, 'status', event.target.value)} aria-label={`Status for ${application.name}`}>
                    {STATUS_OPTIONS.map((status) => <option key={status} value={status}>{status}</option>)}
                  </select>
                </div>
                <dl className="seller-application-details">
                  <div><dt>Contact</dt><dd>{application.email}<br />{application.phone}</dd></div>
                  <div><dt>Makes</dt><dd>{application.craft_type}</dd></div>
                  <div><dt>Experience</dt><dd>{application.experience_years ?? 'Not provided'} years · {application.price_range || 'Price range not provided'}</dd></div>
                </dl>
                <p className="seller-application-copy">{application.about}</p>
                {application.website && <a href={application.website} target="_blank" rel="noopener noreferrer">View portfolio / Instagram</a>}
                {photos.length > 0 && (
                  <div className="seller-application-photos">
                    {photos.map((photo, index) => <a href={photo.url} target="_blank" rel="noopener noreferrer" key={photo.url || index}><img src={photo.url} alt={`${application.name}'s work ${index + 1}`} /></a>)}
                  </div>
                )}
                <label className="form-label mt-3" htmlFor={`seller-note-${application.id}`}>Admin note</label>
                <textarea id={`seller-note-${application.id}`} className="form-control" rows="3" value={application.admin_note || ''} onChange={(event) => updateLocal(application.id, 'admin_note', event.target.value)} />
                <button type="button" className="btn btn-primary-custom rounded-pill mt-3" onClick={() => updateApplication(application)} disabled={savingId === application.id}>
                  {savingId === application.id ? 'Saving…' : 'Save review'}
                </button>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
